import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Zap, Shield, Wind, Layers, Phone, Star, Package, Users } from 'lucide-react';
import { ROUTES, PRICING } from '@/lib/index';

const features = [
  { icon: Wind, label: 'Microperforada', desc: 'Ultra transpirable' },
  { icon: Zap, label: 'Secado Rápido', desc: 'Tecnología avanzada' },
  { icon: Layers, label: 'Elástica 4 Vías', desc: 'Libertad total' },
  { icon: Shield, label: 'Full Print', desc: 'Sin límites de diseño' },
];

const sports = [
  'FÚTBOL', 'BASKETBALL', 'VÓLEY', 'ATLETISMO',
  'CICLISMO', 'BÉISBOL', 'FÚTBOL AM.', 'PÁDEL', 'PESCA', 'EMPRESA',
];

const stats = [
  { icon: Users, value: '500+', label: 'Clientes satisfechos' },
  { icon: Package, value: '10,000+', label: 'Uniformes entregados' },
  { icon: Star, value: '100%', label: 'Calidad garantizada' },
];

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 35 } },
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        {/* Foto de fondo real */}
        <img
          src="/hero-bg.jpeg"
          alt="Dos de Tres uniformes"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ objectPosition: 'center center' }}
        />
        {/* Overlay oscuro para que el texto resalte */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.25) 100%)',
          }}
        />
        {/* Línea neón sutil en la parte inferior */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-primary/40" />

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 40 }}
            >
              {/* Big logo number */}
              <div className="flex items-end gap-4 mb-6">
                <span
                  className="text-8xl md:text-[10rem] font-black text-primary leading-none"
                  style={{ letterSpacing: '-8px', lineHeight: 0.85 }}
                >
                  23
                </span>
                <div className="pb-2">
                  <p className="text-white font-black text-2xl md:text-4xl tracking-widest uppercase leading-tight">
                    DOS DE TRES
                  </p>
                  <p className="text-primary font-bold text-base tracking-[0.3em] uppercase">
                    Diseñado para competir.
                  </p>
                </div>
              </div>

              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl leading-relaxed">
                Uniformes personalizados <span className="text-primary font-bold">full print</span> para todo tipo de deporte y profesión.
                Tecnología premium, máximo rendimiento.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to={ROUTES.ORDER}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-black text-sm tracking-widest uppercase rounded hover:bg-primary/90 transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
                >
                  HACER MI PEDIDO
                  <ChevronRight size={18} />
                </Link>
                <Link
                  to={ROUTES.CATALOG}
                  className="inline-flex items-center gap-2 px-8 py-4 border border-primary text-primary font-bold text-sm tracking-widest uppercase rounded hover:bg-primary/10 transition-all duration-200"
                >
                  VER CATÁLOGO
                </Link>
              </div>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/528333007124?text=Hola,%20me%20interesa%20pedir%20uniformes"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-6 text-sm text-muted-foreground hover:text-primary transition-colors font-mono"
              >
                <Phone size={14} />
                WhatsApp: 833 300 7124
              </a>
            </motion.div>
          </div>
        </div>

        {/* Diagonal slash */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-background"
          style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      {/* SPORTS TICKER */}
      <section className="bg-primary py-3 overflow-hidden">
        <div className="flex gap-8 animate-scroll whitespace-nowrap">
          {[...sports, ...sports].map((s, i) => (
            <span key={i} className="text-primary-foreground font-black text-sm tracking-widest inline-flex items-center gap-4">
              {s} <span className="text-primary-foreground/40">|</span>
            </span>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {features.map(({ icon: Icon, label, desc }) => (
              <motion.div
                key={label}
                variants={item}
                className="bg-card border border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors duration-300 group"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                  <Icon className="text-primary" size={22} />
                </div>
                <p className="font-black text-sm tracking-widest uppercase text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-1">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SPORTS GRID */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200, damping: 40 }}
            className="text-center mb-12"
          >
            <p className="text-primary font-bold text-xs tracking-widest uppercase mb-2">PARA TODOS</p>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-foreground">
              TRABAJAMOS PARA<br />
              <span className="text-primary">CUALQUIER DEPORTE</span>
            </h2>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3">
            {sports.map((sport, i) => (
              <motion.span
                key={sport}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, type: 'spring', stiffness: 300, damping: 25 }}
                className="px-5 py-2 border border-border text-sm font-bold tracking-widest hover:border-primary hover:text-primary transition-colors duration-200 rounded cursor-default"
              >
                {sport}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map(({ icon: Icon, value, label }) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ type: 'spring', stiffness: 200, damping: 35 }}
                className="text-center"
              >
                <Icon className="text-primary mx-auto mb-3" size={32} />
                <p className="text-4xl font-black text-primary mb-1 font-mono">{value}</p>
                <p className="text-sm text-muted-foreground tracking-widest uppercase font-bold">{label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-primary font-bold text-xs tracking-widest uppercase mb-2">PRECIOS TRANSPARENTES</p>
            <h2 className="text-3xl font-black uppercase tracking-tight text-foreground">
              ENTRE MÁS PIDES,<br />
              <span className="text-primary">MÁS AHORRAS</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-3xl mx-auto">
            {PRICING.map((p) => (
              <div
                key={p.label}
                className={`rounded-lg p-4 text-center border transition-all duration-200 ${
                  p.highlight
                    ? 'bg-primary border-primary shadow-lg shadow-primary/20'
                    : 'bg-background border-border'
                }`}
              >
                <p className={`text-[10px] font-black tracking-widest uppercase mb-1 ${p.highlight ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                  {p.label}
                </p>
                <p className={`text-xl font-black font-mono ${p.highlight ? 'text-primary-foreground' : 'text-primary'}`}>
                  {p.price}
                </p>
                <p className={`text-[10px] mt-1 ${p.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                  {p.quantity}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to={ROUTES.PRICING}
              className="inline-flex items-center gap-2 text-sm text-primary font-bold tracking-widest hover:underline"
            >
              VER TODOS LOS PRECIOS <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section
        className="py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, oklch(0.08 0.01 0), oklch(0.14 0.03 75))' }}
      >
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          >
            <h2 className="text-3xl md:text-5xl font-black uppercase text-foreground mb-4 tracking-tight">
              ¿LISTO PARA <span className="text-primary">COMPETIR</span>?
            </h2>
            <p className="text-muted-foreground mb-8 text-lg max-w-lg mx-auto">
              Solicita tu cotización ahora. Envíos a toda la República.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to={ROUTES.ORDER}
                className="px-10 py-4 bg-primary text-primary-foreground font-black text-sm tracking-widest uppercase rounded hover:bg-primary/90 transition-all hover:scale-[1.03] active:scale-[0.97] flex items-center gap-2"
              >
                HACER MI PEDIDO <ChevronRight size={16} />
              </Link>
              <a
                href="https://wa.me/528333007124?text=Hola,%20quiero%20hacer%20un%20pedido"
                target="_blank"
                rel="noreferrer"
                className="px-10 py-4 border border-primary text-primary font-bold text-sm tracking-widest uppercase rounded hover:bg-primary/10 transition-all flex items-center gap-2"
              >
                <Phone size={16} /> WHATSAPP
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        @keyframes scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 20s linear infinite;
          display: inline-flex;
        }
      `}</style>
    </div>
  );
}
